import orderBy from "lodash/orderBy";
import {
  AccountContribution,
  AllocationGroup,
  Holding,
  PerformanceGroup,
} from "@modules/investments/types";
import {
  OrionCostBasisItem,
  OrionReportingResponse,
  OrionReportingCalculation,
} from "@modules/orion/types";
import moment from "moment";
import { DATE_FORMAT, PerformancePeriod } from "./constants";

export const parsePortfolioSummaryData = (
  data: OrionReportingResponse | undefined
) => {
  if (!data) return;

  const activity = data.calculations?.find(
    (c) =>
      c["$type"] === "calculated-activity" && c["id"] === "PortfolioSummary"
  );
  return {
    marketValue: activity?.value ?? 0,
    performance:
      data.calculations?.find((c) => c["$type"] === "calculated-performance")
        ?.performance ?? 0,
    startDate: moment(activity?.startDate).format(DATE_FORMAT),
  };
};

const findActivityCalculationByType = (
  calculations: OrionReportingCalculation[],
  activityType: string
) => {
  return (
    calculations?.find((c) =>
      c?.contextChange?.activityOption?.details?.find(
        (d) => d["activityType"] === activityType
      )
    )?.calculations?.[0]?.value ?? 0
  );
};

export const parseActivitySummaryData = (
  data: OrionReportingResponse | undefined
) => {
  if (!data) return;

  const activityCalculations =
    (data.calculations ?? []).find((c) => c["$type"] === "calculated-list")
      ?.calculations ?? [];
  const beginningMarketValue =
    data.calculations?.find((c) => c["id"] === "BeginningMarketValue")?.value ??
    0;
  const endingMarketValue =
    data.calculations?.find((c) => c["id"] === "EndingMarketValue")?.value ?? 0;
  // Merges (Merge In/Out) are intentionally excluded from inflows/outflows.
  // A merge is an internal re-registration between Orion accounts, and the
  // moved assets are already reflected in Beginning/Ending Market Value.
  // Counting them here would double-count: marketValueChange is a residual
  // (ending - beginning - inflows - outflows), so a merge included as a flow
  // gets subtracted a second time and shows up as a phantom gain/loss. Orion's
  // own Activity Summary (the advisor console) omits merges for the same
  // reason — this keeps us reconciled to it. Do not re-add them unless this
  // firm's Orion Activity Summary template starts including merges too.
  const summary = {
    beginningMarketValue,
    endingMarketValue,
    inflows:
      findActivityCalculationByType(activityCalculations, "Contributions") +
      findActivityCalculationByType(
        activityCalculations,
        "Dividends and Interest"
      ),
    outflows:
      findActivityCalculationByType(activityCalculations, "Distributions") +
      findActivityCalculationByType(activityCalculations, "Advisory Fees Paid"),
    marketValueChange: 0,
  };

  summary.marketValueChange =
    summary.endingMarketValue -
    summary.beginningMarketValue -
    summary.inflows -
    summary.outflows;

  return summary;
};

// Parses the Registration-grouped contributions report into one row per Orion
// account, largest first.
//
// Accounts with no contributions this year are dropped rather than rendered as
// $0: the tile lists money that actually moved, and a household's every dormant
// registration showing "$0" reads as a data problem instead of an accurate zero.
//
// The filter is `!== 0`, NOT `> 0` — negatives are kept on purpose. Orion's
// Contributions bucket is a signed net, so a reversal or correction can leave an
// account negative for the year. Hiding those rows would both conceal real money
// movement the advisor should look at AND overstate the household total, since
// the total is summed from the rows that survive this filter. Do not "fix" this
// to `> 0`.
export const parseContributionsByAccount = (
  data: OrionReportingResponse | undefined
): AccountContribution[] => {
  if (!data) return [];

  const groups =
    data.calculations?.find((c) => c.id === "grouping-registration")
      ?.calculations ?? [];

  return orderBy(
    groups
      .map((group) => ({
        accountName: group.name ?? "",
        contributionsDollars:
          group.calculations?.find((c) => c.id === "contributions-ytd")
            ?.value ?? 0,
      }))
      .filter((row) => row.contributionsDollars !== 0),
    "contributionsDollars",
    ["desc"]
  );
};

export const parseAllocationGroups = (
  data: OrionReportingResponse | undefined
): AllocationGroup[] | null => {
  if (!data) return null;

  const calcs = data.calculations?.find(
    (c) => c.id === "AllocationDetail"
  )?.calculations;
  const totalValue =
    calcs?.reduce((memo, calc) => {
      memo +=
        calc?.calculations?.find((c) => c.id === "EndingMarketValue")?.value ??
        0;
      return memo;
    }, 0) ?? 1;
  return orderBy(
    calcs?.map((calc) => {
      const value =
        calc?.calculations?.find((c) => c.id === "EndingMarketValue")?.value ??
        0;
      return {
        name: calc.name ?? "",
        value,
        percentage: value / totalValue,
      };
    }) ?? [],
    "value",
    ["desc"]
  );
};

// Parses the "Asset"-grouped holdings report into one row per security,
// optionally enriched with cost basis + unrealized gain/loss.
//
// Two Orion feeds are needed because Reporting/Scope cannot produce cost basis:
// it accepts an `ActivityOption { $type: "cost-basis" }` but silently echoes
// market value back, which reads as a populated column and renders every
// gain/loss as $0. Cost basis therefore comes from the dedicated
// Reporting/Activity/CostBasis endpoint and is joined here.
//
// `costBasisRows` is optional and best-effort: without it (or on a failed
// fetch) holdings still render, with cost basis and gain/loss as null → "—".
export const parseHoldings = (
  data: OrionReportingResponse | undefined,
  costBasisRows?: OrionCostBasisItem[]
): Holding[] | null => {
  if (!data) return null;

  // assetId → { costBasis, gainLoss }, where total unrealized gain/loss is
  // long-term + short-term. ACCUMULATE rather than overwrite: the endpoint
  // returns one aggregated row per asset today, but summing stays correct if it
  // ever becomes tax-lot granular (multiple rows per assetId).
  const costBasisByAsset = new Map<
    number,
    { costBasis: number; gainLoss: number }
  >();
  for (const cb of costBasisRows ?? []) {
    // Coerce the key so the report's `groupEntityId` and the feed's `assetId`
    // compare as the same type (guards against one arriving as a string).
    const assetId = Number(cb.assetId);
    const acc = costBasisByAsset.get(assetId) ?? { costBasis: 0, gainLoss: 0 };
    acc.costBasis += cb.costBasis ?? 0;
    acc.gainLoss += (cb.longTermGainLoss ?? 0) + (cb.shortTermGainLoss ?? 0);
    costBasisByAsset.set(assetId, acc);
  }

  const rows =
    data.calculations?.find((c) => c.id === "AllocationDetail")?.calculations ??
    [];

  // Orion returns one row per (account × asset), so a household holding the
  // same fund in three accounts arrives as three rows. Aggregate by security
  // name, tracking the DISTINCT set of asset ids (`groupEntityId`) per security
  // so cost basis is summed once per asset — a repeated asset id cannot
  // double-count, while the same security across different accounts (distinct
  // asset ids) still sums correctly.
  const bySecurity = new Map<
    string,
    {
      units: number;
      price: number | null;
      marketValue: number;
      assetIds: Set<number>;
    }
  >();
  for (const row of rows) {
    const mv = row.calculations?.find((c) => c.id === "EndingMarketValue");
    const name = row.name ?? "";
    const agg = bySecurity.get(name) ?? {
      units: 0,
      price: mv?.price ?? null,
      marketValue: 0,
      assetIds: new Set<number>(),
    };
    agg.units += mv?.units ?? 0;
    agg.marketValue += mv?.value ?? 0;
    // Price is per-share: take the first non-null, never sum or average.
    if (agg.price == null) agg.price = mv?.price ?? null;
    if (row.groupEntityId != null) agg.assetIds.add(Number(row.groupEntityId));
    bySecurity.set(name, agg);
  }

  // Weight is over Earned-managed holdings only — held-away (MX) balances are
  // deliberately outside the denominator. Guard against a zero/negative total
  // (a wholly-negative margin position) so weights degrade to null → "—"
  // rather than rendering NaN%.
  const total = Array.from(bySecurity.values()).reduce(
    (sum, a) => sum + a.marketValue,
    0
  );
  const hasUsableTotal = total > 0;

  let matchedAssets = 0;
  const holdings = orderBy(
    Array.from(bySecurity.entries()).map(([securityName, a]) => {
      let costBasis = 0;
      let gainLoss = 0;
      let resolved = 0;
      a.assetIds.forEach((assetId) => {
        const cb = costBasisByAsset.get(assetId);
        if (cb) {
          costBasis += cb.costBasis;
          gainLoss += cb.gainLoss;
          resolved += 1;
        }
      });
      matchedAssets += resolved;
      // Show cost basis only when EVERY asset of the security resolved. Partial
      // coverage would understate cost basis against the full market value and
      // still read as authoritative — worse than "—".
      const fullyCovered = a.assetIds.size > 0 && resolved === a.assetIds.size;
      return {
        securityName,
        units: a.units,
        price: a.price,
        marketValue: a.marketValue,
        weightPercent: hasUsableTotal ? (a.marketValue / total) * 100 : null,
        costBasis: fullyCovered ? costBasis : null,
        gainLoss: fullyCovered ? gainLoss : null,
      };
    }),
    "marketValue",
    ["desc"]
  );

  // Cost-basis rows came back but none joined → the assetId↔groupEntityId key is
  // likely wrong (renamed/reshaped), not "Orion has no cost basis". Surface it
  // (counts only — no client data) so the two cases aren't confused.
  if ((costBasisRows?.length ?? 0) > 0 && matchedAssets === 0) {
    console.warn(
      `[holdings] cost-basis join matched 0 of ${costBasisRows?.length} rows — check assetId/groupEntityId`
    );
  }

  return holdings;
};

export const parsePortoflioPerformance = (
  data: OrionReportingResponse
): number | undefined => {
  return data?.calculations?.[0]?.calculations?.find(
    (c) => c.id === "portfolio-performance-ytd"
  )?.performance;
};

export const parsePerformanceData = (
  data: OrionReportingResponse
): PerformanceGroup[] => {
  const findPerformance = (
    group: OrionReportingCalculation,
    id: string
  ): number => group.calculations?.find((c) => c.id === id)?.performance ?? 0;
  return orderBy(
    (
      data?.calculations?.[0]?.calculations?.find(
        (c) => c.id === "grouping-registration"
      )?.calculations ?? []
    ).reduce((memo, group) => {
      const value =
        group.calculations?.find((c) => c.id === "current-market-value")
          ?.value ?? 0;
      const performanceYtd = findPerformance(group, "performance-ytd");
      const performance12Mo = findPerformance(group, "performance-12mo");
      const performanceLastYear = findPerformance(
        group,
        "performance-last-year"
      );
      const performanceGroup = {
        name: group.name ?? "",
        value,
        performance: performanceYtd,
        performanceYtd,
        performance12Mo,
        performanceLastYear,
      };
      memo.push(performanceGroup);
      return memo;
    }, [] as PerformanceGroup[]),
    "value",
    ["desc"]
  );
};

export const getPerformanceForPeriod = (
  group: PerformanceGroup,
  period: PerformancePeriod
): number => {
  switch (period) {
    case PerformancePeriod.TwelveMonth:
      return group.performance12Mo;
    case PerformancePeriod.LastYear:
      return group.performanceLastYear;
    case PerformancePeriod.Ytd:
    default:
      return group.performanceYtd;
  }
};

// Recon is at 12:00 pm UTC (8:00am ET), but data from Orion not synced until about 8:10am ET
export const getMaxEndDate = () => {
  const reconTime = moment.utc().set({ hour: 12, minute: 10, second: 0 });
  return moment.utc().diff(reconTime) > 0
    ? moment.utc().subtract(1, "days")
    : moment.utc().subtract(2, "days");
};

export const PERCENT_OF_AUM_THRESHOLD_TO_DISPLAY = 0.01;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const hasAccountAboveTaxesSavedThreshold = (taxData: any) => {
  if (!!taxData) {
    return taxData.data.some(
      (
        x: any // eslint-disable-line @typescript-eslint/no-explicit-any
      ) =>
        x?.attributes?.since_inception_taxes_saved?.pct_of_aum >
        PERCENT_OF_AUM_THRESHOLD_TO_DISPLAY
    );
  }
  return false;
};
